import {
  FeedbackCreateResponseSchema,
  FeedbackCreateSchema,
  type FeedbackCreate,
  type FeedbackCreateResponse,
} from '../contracts/feedback';
import type { RequestFn } from './client';

/**
 * Namespace `client.feedback` — обёртка над `POST /api/feedback` (Block 10,
 * PRD §14 + ADR-0018).
 *
 * **Scope:** `kind='screen_improvement'` (Block 10, ADR-0018) и
 * `kind='partner_review'` (Block 8 «Партнёры», ADR-0082 — отзыв о подрядчике
 * 2.8.4). Метод `create()` принимает любой вариант `FeedbackCreate`
 * (discriminatedUnion) — отдельного метода под отзыв не нужно (ADR-0082 §2,
 * минимизация публичной поверхности, P-012). Вариант `general` (Block 11, B-084)
 * появится в union при реализации блока.
 *
 * **Auth required (ADR-0018 §Q7).** Authorization: Bearer JWT обязателен.
 * MSW handler (Phase 3) и Phase 9+ Edge Worker возвращают 401 без header'а;
 * App.tsx redirect-цепочка (ADR-0014) подхватывает 401 → `/onboarding/lang-city`.
 *
 * **P-009 защита уровня 2 (ADR-0009).** `safeParse` через Zod-схему ДО сетевого
 * вызова — fail fast при невалидном payload (например customTag без 'other'
 * в tags, или 4 фото в attachedPhotosR2Keys). Если type-checker обойдён
 * (`as any`-cast), Zod refinement блокирует request — rejected Promise без
 * фактического hit'а сети. Симметрия с `onboarding.register()` / `decors.match()`.
 *
 * Response парсится через `FeedbackCreateResponseSchema` (`{ id: uuid, status: 'new' }`).
 *
 * References:
 *   - ADR-0018 §Q1 (FeedbackCreateSchema), §Q7 (auth-required 401)
 *   - PRD §14 (Block 10 form)
 *   - P-012 (typed API client), P-013 (relative URL)
 */
export interface FeedbackApi {
  /**
   * POST /api/feedback — отправить фидбек Block 10.
   *
   * @param data — `FeedbackCreate` (Zod-валидирован client-side через safeParse).
   * @returns `FeedbackCreateResponse` — `{ id: uuid, status: 'new' }`.
   * @throws `z.ZodError` если client-side валидация не прошла (нет фактического сетевого вызова).
   * @throws `ApiClientError` при 401 / 400 / network / 5xx / PARSE_ERROR.
   */
  create(data: FeedbackCreate): Promise<FeedbackCreateResponse>;
}

export function createFeedbackApi(request: RequestFn): FeedbackApi {
  return {
    create: (data) => {
      const parsed = FeedbackCreateSchema.safeParse(data);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/feedback',
        body: parsed.data,
        responseSchema: FeedbackCreateResponseSchema,
        auth: true,
      });
    },
  };
}
